<template>
  <div class="grid grid-cols-1 gap-4">
    <div id="note-create-form" :class="{ 'prospect-note-form-wrapper': showNoteForm }">
      <note-form
          v-if="showNoteForm"
          :create-url="createUrl"
          :existing-note-content="existingNoteContent"
          :existing-note-id="existingNoteId"
          :suggest-url="mentionSuggestUrl"
          :activity-types="formActivityTypes"
          :extra-params="extraPostParams"
          :access-key="accessKey"
          :auth-token="authToken"
          :cloudfront-base-url="cloudfrontBaseUrl"
          :endpoint="endpoint"
          v-on:cancel-note-form="removeNoteForm"
          v-on:note-updated="handleNoteUpdated"
          v-on:note-created="handleNoteCreated"
      >
        <template v-slot:after-content>
          <div
            v-if="showStageShortcuts"
            class="form-group m-t-sm"
          >
            <label class="block">Update stage</label>
            <div
              v-for="child in actionableWrestlers"
              :key="'stage-' + child.id"
              class="flex align-items-center gap-x-xs m-b-xxs"
            >
              <span class="text-14 font-semi flex-1" style="min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ child.child_name || 'Unnamed' }}
              </span>
              <select
                v-model="stageChoices[child.id]"
                class="form-control input-sm"
                style="width: auto; display: inline-block;"
              >
                <option value="">— No change —</option>
                <option v-for="s in stageOptions" :key="child.id + '-' + s.value" :value="s.value">{{ s.label }}</option>
              </select>
            </div>
          </div>
          <div
            v-if="showFollowUpControls"
            class="form-group m-t-sm"
          >
            <label class="block">Change follow-up</label>
            <div
              v-for="child in actionableWrestlers"
              :key="'fu-' + child.id"
              class="flex align-items-center gap-x-xs m-b-xxs"
            >
              <span class="text-14 font-semi flex-1" style="min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ child.child_name || 'Unnamed' }}
              </span>
              <select
                v-model="followUpChoices[child.id]"
                class="form-control input-sm"
                style="width: auto; display: inline-block;"
              >
                <option value="">— No change —</option>
                <option v-if="child.needs_follow_up" value="clear">Clear follow-up</option>
                <option v-else value="add">Add follow-up</option>
              </select>
            </div>
          </div>
        </template>
      </note-form>
    </div>
    <div v-if="!showNoteForm && asTimeline" class="flex gap-x-sm flex-wrap m-b-xs">
      <button type="button" class="timeline-action-btn" @click="openAddNote">
        <i class="fa-solid fa-pencil"></i>
        Add Note
      </button>
      <button
        v-if="hasActivityTypes"
        type="button"
        class="timeline-action-btn"
        @click="openLogContact"
      >
        <i class="fa-solid fa-phone"></i>
        Log Contact
      </button>
    </div>
    <div v-else-if="!showNoteForm" class="flex gap-x-sm flex-wrap">
      <div class="notepad-card flex cursor-pointer" @click="openAddNote">
        + Add Note
      </div>
      <div
        v-if="hasActivityTypes"
        class="notepad-card flex cursor-pointer"
        @click="openLogContact"
      >
        + Log Contact
      </div>
    </div>
    <paged-list
      response-key="notes"
      object-type="Note"
      help-text=""
      :base-url="baseUrl"
      empty-text=" "
      :container-class="listContainerClass"
    >
      <template v-slot:list="slotProps">
        <div
          v-for="ev in eventsBetween(slotProps.result, slotProps.prevResult)"
          :key="'stage-event-' + ev.id"
          class="timeline-entry"
        >
          <span class="timeline-marker" :class="{ 'is-manual': stageEventIsManual(ev) }">
            <i class="fa-solid fa-right-left"></i>
          </span>
          <div class="timeline-event-body flex flex-wrap align-items-baseline gap-x-xs">
            <span class="text-14 text-gray-600">
              <span class="font-semi text-gray-900">{{ ev.child_name || 'Wrestler' }}</span>
              {{ stageEventDescription(ev) }}
            </span>
            <span
              v-if="stageEventVia(ev)"
              class="p-x-xs rounded-xs text-12 font-semi"
              :class="stageEventBadgeClass(ev)"
            >{{ stageEventVia(ev) }}</span>
            <span class="text-12 text-gray-400" :title="stageEventFullDate(ev)">{{ stageEventDate(ev) }}</span>
          </div>
        </div>
        <div v-if="asTimeline" :class="noteEntryClass(slotProps)">
          <span class="timeline-marker">
            <i :class="noteMarkerIcon(slotProps.result)"></i>
          </span>
          <div class="timeline-event-body">
            <div class="flex flex-wrap align-items-baseline gap-x-xs">
              <span class="text-14 text-gray-600">
                <span class="font-semi text-gray-900">{{ entryHeader(slotProps.result) }}</span>
                by {{ slotProps.result.author.display_name }}
              </span>
              <span class="text-12 text-gray-400" :title="createdAt(slotProps.result)">{{ shortCreatedAt(slotProps.result) }}</span>
              <a
                v-if="isAuthorOf(slotProps.result)"
                class="text-12"
                @click.prevent="showUpdateFormFor(slotProps.result)"
              >Edit</a>
            </div>
            <div class="timeline-note-content trix-content" v-html="slotProps.result.content"></div>
          </div>
        </div>
        <div v-else class="notepad-card flex w-full">
          <div class="flex-col flex m-l-m w-full">
            <div class="flex justify-space-between align-items-baseline">
              <h4 class="m-y-none">
                <span class="font-heavy">{{ entryHeader(slotProps.result) }}</span>
                by {{ slotProps.result.author.display_name }}
              </h4>
              <div class="flex gap-x-xs font-small">
                <p v-if="isAuthorOf(slotProps.result)">
                  <a @click.prevent="showUpdateFormFor(slotProps.result)">Edit</a>
                </p>
                <p class="color-gray-500 m-y-none">
                  {{ createdAt(slotProps.result) }}
                </p>
              </div>
            </div>
            <div class="m-t-sm trix-content" v-html="slotProps.result.content"></div>
          </div>
        </div>
      </template>
      <template v-slot:list-footer="footerProps">
        <div
          v-for="(ev, idx) in trailingEvents(footerProps.lastResult, footerProps.noMoreData)"
          :key="'stage-event-' + ev.id"
          class="timeline-entry"
          :class="{ 'is-last': idx === trailingEvents(footerProps.lastResult, footerProps.noMoreData).length - 1 }"
        >
          <span class="timeline-marker" :class="{ 'is-manual': stageEventIsManual(ev) }">
            <i class="fa-solid fa-right-left"></i>
          </span>
          <div class="timeline-event-body flex flex-wrap align-items-baseline gap-x-xs">
            <span class="text-14 text-gray-600">
              <span class="font-semi text-gray-900">{{ ev.child_name || 'Wrestler' }}</span>
              {{ stageEventDescription(ev) }}
            </span>
            <span
              v-if="stageEventVia(ev)"
              class="p-x-xs rounded-xs text-12 font-semi"
              :class="stageEventBadgeClass(ev)"
            >{{ stageEventVia(ev) }}</span>
            <span class="text-12 text-gray-400" :title="stageEventFullDate(ev)">{{ stageEventDate(ev) }}</span>
          </div>
        </div>
      </template>
    </paged-list>
  </div>
</template>
<script>
import NoteForm from './note_form.vue';
import PagedList from './paged_list.vue';

export default {
  name: 'notes-list',
  components: {
    PagedList,
    NoteForm,
  },
  props: {
    // GET paginated notes from this URL
    baseUrl: {
      type: String,
      required: true,
    },
    // POST new notes to this URL
    createUrl: {
      type: String,
      required: true,
    },
    currentAuthorId: {
      type: Number,
      required: true,
    },
    currentAuthorType: {
      type: String,
      required: true,
    },
    mentionSuggestUrl: {
      type: String,
      required: false,
    },
    accessKey: {
      type: String,
      required: false,
    },
    authToken: {
      type: String,
      required: false,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: false,
    },
    endpoint: {
      type: String,
      required: false,
    },
    // When non-empty, enables the "Log Contact" button and the activity type
    // dropdown in note_form. Each entry: { value: String, label: String }
    activityTypes: {
      type: Array,
      default: () => [],
    },
    // When provided, the create form shows per-wrestler stage dropdowns so a
    // coach can update stage(s) in the same submit as the note/contact log,
    // plus follow-up resolution checkboxes for any flagged children.
    family: {
      type: Object,
      default: null,
    },
    // Stage-change audit rows ({ id, child_name, from_stage, to_stage,
    // changed_at, changed_via, changed_by }) interleaved chronologically
    // with the notes so the timeline shows system vs. manual transitions.
    timelineEvents: {
      type: Array,
      default: () => [],
    },
    // Renders the list as an activity feed — vertical rail + icon markers
    // unifying note cards and stage events (see .prospect-timeline in
    // leads.scss). Off by default so plain note lists (wrestler profile)
    // keep their stacked-card layout.
    asTimeline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      noteForEdit: null,
      showNoteForm: false,
      formMode: 'note', // 'note' or 'contact'
      stageChoices: {},
      // Per-child string: '' (no change) | 'clear' (clear follow-up flag) | 'add' (set flag).
      followUpChoices: {},
    };
  },
  computed: {
    existingNoteContent() {
      return this.noteForEdit?.content ?? null;
    },
    existingNoteId() {
      return this.noteForEdit?.id ?? null;
    },
    hasActivityTypes() {
      return this.activityTypes.length > 0;
    },
    formActivityTypes() {
      return this.formMode === 'contact' ? this.activityTypes : [];
    },
    actionableWrestlers() {
      if (!this.family || !this.family.prospects) return [];
      const actionable = ['inquiry', 'trial_scheduled', 'trialing', 'trial_complete'];
      return this.family.prospects.filter((p) => actionable.includes(p.stage));
    },
    showStageShortcuts() {
      // Only surface the shortcut for new notes/contacts, not while editing an existing entry.
      return this.existingNoteId == null && this.actionableWrestlers.length > 0;
    },
    showFollowUpControls() {
      return this.existingNoteId == null && this.actionableWrestlers.length > 0;
    },
    stageOptions() {
      return [
        { value: 'trial_scheduled', label: 'Trial Scheduled' },
        { value: 'trialing', label: 'Trialing' },
        { value: 'trial_complete', label: 'Trial Complete' },
        { value: 'converted', label: 'Converted' },
        { value: 'didnt_join', label: "Didn't Join" },
        { value: 'archived', label: 'Archived' },
      ];
    },
    listContainerClass() {
      return this.asTimeline ? 'prospect-timeline' : 'grid grid-cols-1 gap-4';
    },
    sortedTimelineEvents() {
      return this.timelineEvents
        .slice()
        .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
    },
    extraPostParams() {
      const params = {};
      const clearIds = Object.keys(this.followUpChoices)
        .filter((id) => this.followUpChoices[id] === 'clear')
        .map((id) => parseInt(id, 10));
      const addIds = Object.keys(this.followUpChoices)
        .filter((id) => this.followUpChoices[id] === 'add')
        .map((id) => parseInt(id, 10));
      if (clearIds.length > 0) params.clear_follow_up_for = clearIds;
      if (addIds.length > 0) params.add_follow_up_for = addIds;
      return params;
    },
  },
  methods: {
    openAddNote() {
      this.formMode = 'note';
      this.resetChoices();
      this.showNoteCreateForm();
    },
    openLogContact() {
      this.formMode = 'contact';
      this.resetChoices();
      this.showNoteCreateForm();
    },
    resetChoices() {
      const nextStages = {};
      const nextFollowUps = {};
      this.actionableWrestlers.forEach((child) => {
        nextStages[child.id] = '';
        nextFollowUps[child.id] = '';
      });
      this.stageChoices = nextStages;
      this.followUpChoices = nextFollowUps;
    },
    showNoteCreateForm() {
      this.showNoteForm = true;
      this.$nextTick(() => {
        const el = document.getElementById('note-create-form');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      });
    },
    createdAt(note) {
      return moment(note.created_at).format('MMM Do, YYYY hh:mm a');
    },
    // Timeline entries show the short date; the full timestamp lives in the
    // title tooltip to keep the feed rows scannable.
    shortCreatedAt(note) {
      return moment(note.created_at).format('MMM D, YYYY');
    },
    // The notes list renders newest-first, so each stage event slots in
    // above the first note that's older than it. Partition boundaries are
    // [note time, previous note time) — disjoint across the whole list.
    eventsBetween(note, prevNote) {
      const noteTime = new Date(note.created_at).getTime();
      const prevTime = prevNote ? new Date(prevNote.created_at).getTime() : null;
      return this.sortedTimelineEvents.filter((ev) => {
        const t = new Date(ev.changed_at).getTime();
        return t >= noteTime && (prevTime === null || t < prevTime);
      });
    },
    // Events older than the oldest visible note. Held back until every page
    // has loaded so they don't render above notes still being fetched.
    trailingEvents(lastResult, noMoreData) {
      if (!noMoreData) return [];
      if (!lastResult) return this.sortedTimelineEvents;
      const lastTime = new Date(lastResult.created_at).getTime();
      return this.sortedTimelineEvents.filter((ev) => new Date(ev.changed_at).getTime() < lastTime);
    },
    stageEventLabel(stage) {
      const labels = {
        inquiry: 'Interested',
        trial_scheduled: 'Trial Scheduled',
        trialing: 'Trialing',
        trial_complete: 'Trial Complete',
        converted: 'Converted',
        didnt_join: "Didn't Join",
        archived: 'Archived',
      };
      return labels[stage] || stage;
    },
    stageEventDescription(ev) {
      if (!ev.from_stage) {
        return `added as ${this.stageEventLabel(ev.to_stage)}`;
      }
      return `moved from ${this.stageEventLabel(ev.from_stage)} to ${this.stageEventLabel(ev.to_stage)}`;
    },
    stageEventIsAutomatic(ev) {
      return ['trial_registration', 'check_in', 'trial_expired', 'paid_registration', 'subscription'].includes(ev.changed_via);
    },
    // True when a person made the change (drives the blue marker + badge).
    // Mirrors stageEventVia: creation rows from public inquiry forms are
    // recorded as "manual" but no staff member acted, so they stay neutral.
    stageEventIsManual(ev) {
      if (this.stageEventIsAutomatic(ev)) return false;
      // backfill/bulk_archive carry a changed_by for provenance, but no one
      // moved these by hand — keep them neutral rather than crediting a coach.
      if (ev.changed_via === 'backfill' || ev.changed_via === 'bulk_archive') return false;
      if (ev.changed_by && ev.changed_by.full_name) return true;
      return !!ev.from_stage;
    },
    stageEventVia(ev) {
      const autoLabels = {
        trial_registration: 'Automatic · trial sign-up',
        check_in: 'Automatic · class check-in',
        trial_expired: 'Automatic · trial passes expired',
        paid_registration: 'Automatic · paid registration',
        subscription: 'Automatic · subscription',
      };
      if (autoLabels[ev.changed_via]) return autoLabels[ev.changed_via];
      if (ev.changed_via === 'backfill') return 'Imported';
      if (ev.changed_via === 'bulk_archive') return 'Bulk cleanup';
      const name = ev.changed_by && ev.changed_by.full_name;
      if (name) return `Manual · ${name}`;
      // Creation rows from the public inquiry form are recorded as "manual"
      // but no staff member did it — show nothing rather than mislead.
      if (!ev.from_stage) return '';
      return 'Manual';
    },
    stageEventBadgeClass(ev) {
      return this.stageEventIsManual(ev) ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600';
    },
    stageEventDate(ev) {
      return moment(ev.changed_at).format('MMM D, YYYY');
    },
    stageEventFullDate(ev) {
      return moment(ev.changed_at).format('MMM Do, YYYY hh:mm a');
    },
    noteMarkerIcon(note) {
      if (!note.activity_type) return 'fa-solid fa-pencil';
      const icons = {
        phone_call: 'fa-solid fa-phone',
        sms: 'fa-solid fa-comment',
        email: 'fa-solid fa-envelope',
        in_person: 'fa-regular fa-handshake',
      };
      // 'other' and any future contact types fall back to a generic bubble.
      return icons[note.activity_type] || 'fa-regular fa-comment';
    },
    // A note is the timeline's final entry only once every page has loaded
    // and no stage events are older than it (those render in list-footer).
    noteEntryClass(slotProps) {
      const isLast = slotProps.isLastResult
        && slotProps.noMoreData
        && this.trailingEvents(slotProps.result, true).length === 0;
      return isLast ? 'timeline-entry is-last' : 'timeline-entry';
    },
    isAuthorOf(note) {
      return note.author.id === this.currentAuthorId
        && note.author.author_type === this.currentAuthorType;
    },
    activityTypeLabel(value) {
      const match = this.activityTypes.find((t) => t.value === value);
      return match ? match.label : value;
    },
    entryHeader(note) {
      if (note.activity_type) {
        return this.activityTypeLabel(note.activity_type);
      }
      return 'Note';
    },
    showUpdateFormFor(note) {
      // Make sure the old trix editor / form gets blown away
      this.noteForEdit = null;
      this.showNoteForm = false;

      // Create the new one
      this.$nextTick(function () {
        this.noteForEdit = note;
        this.formMode = note.activity_type ? 'contact' : 'note';
        this.showNoteCreateForm();
      });
    },
    handleNoteCreated() {
      this.emitStageChanges();
      this.removeNoteForm();
      this.$emit('note-created');
    },
    emitStageChanges() {
      this.actionableWrestlers.forEach((child) => {
        const picked = this.stageChoices[child.id];
        if (!picked || picked === child.stage) return;
        this.$emit('stage-change', { child, newStage: picked, extra: {} });
      });
    },
    handleNoteUpdated() {
      this.removeNoteForm();
      this.$emit('note-updated');
    },
    removeNoteForm() {
      this.noteForEdit = null;
      this.showNoteForm = false;
      this.formMode = 'note';
    },
  },
};
</script>
